import React, { useEffect, useState, useRef } from 'react';
import { useSocket } from '../../contexts/SocketContext';
import { useNotification } from '../../shared/hooks/useNotification';
import { Notification } from '../ui/Notification';
import { useAuth } from '../../shared/contexts/AuthContext';
import { displayTransactionStatus } from '../../shared/services/transactionService';

interface PaymentNotificationProps {
  onClose?: () => void;
}

const PaymentNotification: React.FC<PaymentNotificationProps> = ({ onClose }) => {
  const { socket, isConnected } = useSocket();
  const { notification, showNotification, hideNotification } = useNotification();
  const { user } = useAuth();
  const [creditNotificationAudio, setCreditNotificationAudio] = useState<HTMLAudioElement | null>(null);
  const [debitNotificationAudio, setDebitNotificationAudio] = useState<HTMLAudioElement | null>(null);
  // Track transactions we've already notified about (to avoid duplicates)
  const notifiedTransactionsRef = useRef<Set<string>>(new Set());
  // Track previous status of transactions to detect verification
  const transactionStatusRef = useRef<Map<string, string>>(new Map());
  // Batch update notifications
  const updateBatchRef = useRef<Set<string>>(new Set());
  const verificationBatchRef = useRef<Set<string>>(new Set());
  const updateBatchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Preload notification sounds
  useEffect(() => {
    try {
      const creditAudio = new Audio('/sounds/payment_received_notification.wav');
      creditAudio.preload = 'auto';
      setCreditNotificationAudio(creditAudio);
    } catch (e) {
      console.log('Could not load credit payment notification sound:', e);
      setCreditNotificationAudio(null);
    }

    try {
      const debitAudio = new Audio('/sounds/payment_sent_notification.wav');
      debitAudio.preload = 'auto';
      setDebitNotificationAudio(debitAudio);
    } catch (e) {
      console.log('Could not load debit payment notification sound:', e);
      setDebitNotificationAudio(null);
    }
  }, []);

  // Function to play notification sound based on transaction type
  const playNotificationSound = (transactionType: string) => {
    const audio = transactionType === 'debit' ? debitNotificationAudio : creditNotificationAudio;
    if (audio) {
      try {
        audio.currentTime = 0;
        audio.play().catch(error => {
          console.log('Could not play payment notification sound:', error);
        });
      } catch (error) {
        console.log('Error playing payment notification sound:', error);
      }
    }
  };

  useEffect(() => {
    if (!socket || !isConnected || !user || user.permissionRole !== 'admin') return;

    const handleNewTransaction = (data: any) => {
      // This event is ONLY for NEW transactions (not updates)
      // Show notification for CONFIRMED or VERIFIED status (all payment methods)
      const displayStatus = displayTransactionStatus(data);
      const shouldNotify =
        displayStatus === 'confirmed' || displayStatus === 'verified';
      
      if (shouldNotify && !notifiedTransactionsRef.current.has(data.transaction_id)) {
        notifiedTransactionsRef.current.add(data.transaction_id);
        // Store initial status
        transactionStatusRef.current.set(data.transaction_id, displayStatus);
        
        const amount = formatCurrency(data.transaction_amount, data.transaction_currency);
        const transactionType = data.transaction_type || 'credit';
        
        // Different messages based on status
        let message: string;
        if (displayStatus === 'verified') {
          // New transaction that's already verified (from appointment booking)
          message = transactionType === 'debit' 
            ? `Payment ${amount} verified.`
            : `Payment ${amount} verified.`;
        } else {
          // New transaction with CONFIRMED status
          message = transactionType === 'debit' 
            ? `Payment ${amount} confirmed.`
            : `Payment ${amount} confirmed.`;
        }
        
        showNotification(message, 'success');
        
        // Play appropriate notification sound based on transaction type (ONLY for new transactions)
        playNotificationSound(transactionType);
      }
    };

    const handleTransactionUpdate = (data: any) => {
      // This event is ONLY for UPDATES (not new transactions)
      const previousStatus = transactionStatusRef.current.get(data.transaction_id);
      const currentStatus = displayTransactionStatus(data);
      
      // If we haven't tracked this transaction before and it's already confirmed/verified,
      // it means it was already confirmed when the component loaded. Mark it as notified
      // without showing a notification (this is not a new confirmation, just an update).
      if (!previousStatus && (currentStatus === 'confirmed' || currentStatus === 'verified')) {
        // Transaction was already confirmed/verified - mark as notified but don't show notification
        notifiedTransactionsRef.current.add(data.transaction_id);
        transactionStatusRef.current.set(data.transaction_id, currentStatus);
        return; // Don't show notification for already-confirmed transactions
      }
      
      // Update stored status
      transactionStatusRef.current.set(data.transaction_id, currentStatus);
      
      // Check if this is a new confirmation (pending → confirmed)
      // This happens for Stripe/Interac payments that start as pending then get confirmed
      const isNewConfirmation = previousStatus && 
                                 previousStatus !== 'confirmed' && 
                                 previousStatus !== 'verified' && 
                                 currentStatus === 'confirmed';
      
      // Check if this is a verification (confirmed → verified)
      const isVerification = previousStatus === 'confirmed' && currentStatus === 'verified';
      
      // If this is a new confirmed payment, show notification and play sound (ONLY ONCE)
      if (isNewConfirmation && !notifiedTransactionsRef.current.has(data.transaction_id)) {
        notifiedTransactionsRef.current.add(data.transaction_id);
        
        const amount = formatCurrency(data.transaction_amount, data.transaction_currency);
        const transactionType = data.transaction_type || 'credit';
        
        showNotification(`Payment ${amount} confirmed.`, 'success');
        playNotificationSound(transactionType);
        return; // Don't process as regular update
      }
      
      // If this is a verification (confirmed → verified), only notify if we haven't notified for this status change yet
      // Skip verification notification if we already showed confirmation (to avoid duplicates)
      // Only show verification if it's a separate action (not just a status change after confirmation)
      if (isVerification) {
        // Don't show separate verification notification if we just showed confirmation
        // The transaction is already confirmed, verification is just an additional step
        // Skip to avoid duplicate notifications
        return;
      }
      
      // For other updates (not confirmations or verifications), batch them together
      // Skip generic updates to avoid noise - these are just field updates (notes, name, etc.)
      // and should not trigger notifications
      return; // Don't show any notification for field updates
    };

    // Listen for payment-related events
    // Note: Backend only emits transaction:newTransaction and transaction:updateTransaction
    socket.on('transaction:newTransaction', handleNewTransaction);
    socket.on('transaction:updateTransaction', handleTransactionUpdate);

    return () => {
      socket.off('transaction:newTransaction', handleNewTransaction);
      socket.off('transaction:updateTransaction', handleTransactionUpdate);
      
      // Clear any pending batch timeout
      if (updateBatchTimeoutRef.current) {
        clearTimeout(updateBatchTimeoutRef.current);
        updateBatchTimeoutRef.current = null;
      }
      updateBatchRef.current.clear();
      verificationBatchRef.current.clear();
    };
  }, [socket, isConnected, showNotification, user]);

  const formatCurrency = (amount: number, currency: string) => {
    return `${amount.toFixed(2)} ${currency}`;
  };

  // Don't render anything if user is not admin
  if (!user || user.permissionRole !== 'admin') {
    return null;
  }

  return (
    <Notification
      open={notification.open}
      message={notification.message}
      severity={notification.severity}
      onClose={hideNotification}
      autoHideDuration={10000}
    />
  );
};

export default PaymentNotification;
